import { Navigate, Outlet, useLocation } from "react-router-dom"

import { usePermissions, type Permissao } from "@/hooks/usePermissions"

/**
 * Guard de permissão (UX). Redireciona para /403 quando o usuário
 * autenticado não possui nenhuma das permissões informadas.
 * A segurança real é sempre validada no backend (@PreAuthorize).
 *
 * ──────────────────────────────────────────────────────────────────
 * USO OBRIGATÓRIO: toda rota de módulo DEVE ser envolvida.
 * ──────────────────────────────────────────────────────────────────
 *
 * No router.tsx, crie um objeto de rota SEM path e coloque as
 * rotas do módulo como children:
 *
 *   {
 *     element: <RequirePermission any={[PERMISSOES.modulo.permissao]} />,
 *     children: [
 *       { path: "modulo",         element: <Suspense><Dash /></Suspense> },
 *       { path: "modulo/sub",     element: <Suspense><Pagina /></Suspense> },
 *     ],
 *   }
 *
 * Excepciones: / (homepage), /login, /auth/callback, /403
 * não exigem RequirePermission.
 *
 * @see src/hooks/usePermissions.ts  — definição de PERMISSOES e ROLE_POR_PERMISSAO
 * @see src/app/router.tsx           — aplicação dos guards nas rotas
 */
export function RequirePermission({ any }: { any: Permissao[] }) {
  const { has } = usePermissions()
  const location = useLocation()

  const allowed = any.some((p) => has(p))
  if (!allowed) {
    return <Navigate to="/403" replace state={{ from: location }} />
  }
  return <Outlet />
}
